import React, {forwardRef, useEffect, useRef} from 'react';

import mergeRefs from 'merge-refs';

import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import comboBoxStyles from './combo-box.component.module.styl';

import type {IconType} from '@pb/kit-component-icon';
import type {InputProps} from '@pb/kit-component-input-v2';
import type {ComponentPropsWithoutRef, ReactNode} from 'react';

const classes = hashedClasses(comboBoxStyles);
const classNames = withCssModule(comboBoxStyles);

type ItemProps = {
    caption: string,
    iconBefore?: IconType,
    meta?: ReactNode,
};

type NativeProps = Omit<InputProps, 'value'>;

export interface ComboBoxInputProps extends NativeProps {
    /**
     * When `true`, the input displays multiple selected captions joined by `", "` and
     * the listbox stays open after each selection for toggling. Inferred automatically
     * by `<ComboBox>` when `value` is an array.
     */
    multiselectable?: boolean,

    /**
     * Text shown in the input when no item is selected, or when the selected item
     * is the `<DefaultItem>` (whose caption becomes the placeholder instead).
     */
    placeholder?: string,

    /**
     * Optional transform applied to the joined caption string before it is rendered
     * in the input. Receives the comma-separated captions of all selected items as a
     * single string and should return the display string.
     *
     * @example
     * captionTransform={caps => `${caps.split(',').length} selected`}
     */
    captionTransform?: (captions: string) => string,

    /**
     * Icon rendered to the right of the selected caption, before the chevron.
     * Accepts an `IconType` constant from `@pb/kit-component-icon`.
     */
    iconAfter?: IconType,

    /**
     * When `true`, the `meta` node of the selected item is shown inside the input
     * field (single-select only — ignored in multi-select mode).
     */
    showSelectedItemMeta?: boolean,

    /**
     * Props of the currently selected item(s) derived by `<ComboBox>` from the
     * `children` + `value`. Provided internally — do not set manually when using
     * `<ComboBox>`; only relevant when composing `ComboBoxInput` standalone.
     */
    selectedItemsProps: ItemProps[],

    /**
     * `true` when the sole selected value matches the `<DefaultItem>` value.
     * In that case the caption is shown as a placeholder (greyed) instead of a
     * selected value, and the input value is set to `""`.
     */
    isDefaultItemSelected?: boolean,

    /**
     * Called when the user presses Enter or Space while the input is focused.
     * In `<ComboBox>` this either opens the listbox (if closed) or confirms the
     * currently focused item (if open).
     */
    onKeyDownHandler?: () => void,
}

/**
 * ComboBox Input component
 *
 * Renders a ComboBox Input. For internal use only.
 */
export const ComboBoxInput = forwardRef<HTMLInputElement, ComboBoxInputProps>(function ComboBoxInput(
    props, ref,
) {
    const {
        captionTransform,
        placeholder,
        color,
        showSelectedItemMeta,
        multiselectable,
        selectedItemsProps,
        isDefaultItemSelected,
        onKeyDownHandler,
        ...nativeProps
    } = props;
    const comboboxInputRef = useRef<HTMLInputElement>(null);

    const renderIconBefore = () => {
        if (multiselectable) {
            return null;
        }
        const iconBefore = selectedItemsProps[0]?.iconBefore;

        if (iconBefore) {
            return (
                <Icon
                    aria-hidden={true}
                    type={iconBefore}
                />
            );
        }

        return null;
    };

    const isMetaDataShown = showSelectedItemMeta && !multiselectable && selectedItemsProps[0]?.meta;

    let captions = selectedItemsProps.map(({caption}) => caption).join(', ');
    if (captionTransform) {
        captions = captionTransform(captions);
    }

    const value = isDefaultItemSelected
        ? ''
        : captions;


    useEffect(() => {
        if (comboboxInputRef.current && value && value.length !== 0) {
            const input = comboboxInputRef.current;

            setTimeout(() => {
                input.scrollLeft = 0;
            }, 0);
        }
    }, [value]);

    return (
        <Input
            inputComponent={ReadonlyInput as InputProps['inputComponent']}
            placeholder={
                isDefaultItemSelected
                    ? captions
                    : placeholder
            }
            value={value}
            autoCapitalize="none"
            spellCheck="false"
            aria-autocomplete="list"
            color={color}
            startAdornment={renderIconBefore()}
            endAdornment={(
                <Group gap="s" alignItems="center" className="pointer-events-none">
                    {isMetaDataShown ? (
                        <span className={classNames('combo-box__meta-data pointer-events-auto')}>
                            {selectedItemsProps[0].meta}
                        </span>
                    ) : null}
                    {color === 'ERROR' ? (
                        <Icon type={Icons.WARNING} className="pointer-events-auto error-color"/>
                    ) : null}
                    <Icon
                        type={Icons.ANGLE_DOWN}
                        className={classes['combo-box__icon']}
                    />
                </Group>
            )}
            {...nativeProps}
            onKeyDown={event => {
                if (
                    event.key === 'Enter' || event.key === ' '
                ) {
                    onKeyDownHandler?.();
                    event.preventDefault();
                }
                nativeProps.onKeyDown?.(event);
            }}
            className={classNames('combo-box_input', nativeProps.className)}
            ref={mergeRefs(ref, comboboxInputRef)}
        />
    );
});

ComboBoxInput.displayName = 'ComboBoxInput';

type ReadonlyInputProps = ComponentPropsWithoutRef<'input'>;

const ReadonlyInput = forwardRef<HTMLInputElement, ReadonlyInputProps>((props, ref) => (
    <input
        ref={ref}
        readOnly={true}
        {...props}
    />
));

ReadonlyInput.displayName = 'ReadonlyInput';
